import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import type { Config, Driver } from 'driver.js';
import { useAuthContext } from '../contexts/AuthContext';
import { isOrgManager } from '../lib/orgRoles';
import { usePracticeWalkthroughContext } from '../contexts/PracticeWalkthroughContext';
import api from '../lib/api';
import {
  CURRENT_NEW_PRACTICE_TOUR_VERSION,
  CURRENT_PRACTICE_SESSION_TOUR_VERSION,
} from '../constants/practiceWalkthrough';
import { buildNewPracticeWalkthroughSteps } from '../practiceWalkthrough/buildNewPracticeWalkthroughSteps';
import {
  buildPracticeSessionWalkthroughSteps,
  type PracticeSessionWalkthroughFlags,
} from '../practiceWalkthrough/buildPracticeSessionWalkthroughSteps';
import {
  STORAGE_PENDING_NEW_MANUAL,
  STORAGE_PENDING_SESSION_MANUAL,
} from '../practiceWalkthrough/storageKeys';
import { parsePracticeSessionId } from '../practiceWalkthrough/parsePracticeSessionId';

async function postNewPracticeTourComplete(): Promise<void> {
  try {
    await api.post('/auth/me/practice-tour/new-practice/complete');
  } catch {
    // non-blocking
  }
}

async function postPracticeSessionTourComplete(): Promise<void> {
  try {
    await api.post('/auth/me/practice-tour/session/complete');
  } catch {
    // non-blocking
  }
}

export function PracticeWalkthrough() {
  const { user } = useAuthContext();
  const { manualNewPracticeRequest, manualPracticeSessionRequest } =
    usePracticeWalkthroughContext();
  const location = useLocation();
  const pathname = location.pathname;

  const newDriverRef = useRef<Driver | null>(null);
  const sessionDriverRef = useRef<Driver | null>(null);

  const autoNewStartedRef = useRef(false);
  const autoSessionStartedRef = useRef(false);
  const newPracticeScheduleGuardRef = useRef(false);
  const sessionScheduleGuardRef = useRef(false);
  const lastManualNewRequestRef = useRef(0);
  const lastManualSessionRequestRef = useRef(0);

  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState<boolean | null>(null);
  const [newPracticeTourVersion, setNewPracticeTourVersion] = useState<number | null>(null);
  const [sessionTourVersion, setSessionTourVersion] = useState<number | null>(null);
  const [orgFeatures, setOrgFeatures] = useState<string[] | null>(null);

  const runNewPracticeTourRef = useRef<(isAuto: boolean) => void>(() => {});
  const runSessionTourRef = useRef<(isAuto: boolean) => void>(() => {});

  const destroyNewDriver = useCallback(() => {
    newDriverRef.current?.destroy();
    newDriverRef.current = null;
  }, []);

  const destroySessionDriver = useCallback(() => {
    sessionDriverRef.current?.destroy();
    sessionDriverRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api
        .get<{
          newPracticeTourVersionCompleted?: number;
          practiceSessionTourVersionCompleted?: number;
        }>('/auth/me')
        .then((res) => ({
          newV: res.data.newPracticeTourVersionCompleted ?? 0,
          sessionV: res.data.practiceSessionTourVersionCompleted ?? 0,
        }))
        .catch(() => ({
          newV: CURRENT_NEW_PRACTICE_TOUR_VERSION,
          sessionV: CURRENT_PRACTICE_SESSION_TOUR_VERSION,
        })),
      api
        .get<{ questionnaireCompleted?: boolean; features?: string[] }>('/orgs/me')
        .then((res) => ({
          qc: res.data.questionnaireCompleted ?? true,
          features: res.data.features ?? [],
        }))
        .catch(() => ({ qc: true, features: [] as string[] })),
    ]).then(([versions, org]) => {
      if (cancelled) return;
      setNewPracticeTourVersion(versions.newV);
      setSessionTourVersion(versions.sessionV);
      setQuestionnaireCompleted(org.qc);
      setOrgFeatures(org.features);
      setPrefsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const runNewPracticeTour = useCallback(
    (isAuto: boolean) => {
      if (!user) return;
      if (isAuto && questionnaireCompleted === false) return;
      if (pathname !== '/practice/new') return;
      if (orgFeatures === null) return;

      if (!isAuto) {
        try {
          sessionStorage.removeItem(STORAGE_PENDING_NEW_MANUAL);
        } catch {
          // ignore
        }
      }

      destroySessionDriver();

      const canPracticeScheduling = orgFeatures.includes('practice_scheduling');
      const canMultiPersona = orgFeatures.includes('multi_persona');
      const canMultiUser = orgFeatures.includes('multi_user');

      const steps = buildNewPracticeWalkthroughSteps({
        canPracticeScheduling,
        canMultiPersona,
        canMultiUser,
      });

      const onDestroyed: NonNullable<Config['onDestroyed']> = () => {
        void postNewPracticeTourComplete();
        newDriverRef.current = null;
        setNewPracticeTourVersion(CURRENT_NEW_PRACTICE_TOUR_VERSION);
      };

      destroyNewDriver();

      const d = driver({
        showProgress: true,
        smoothScroll: true,
        allowClose: true,
        overlayOpacity: 0.55,
        stagePadding: 8,
        popoverClass: 'akoreps-driver-popover',
        steps,
        onDestroyed,
      });

      newDriverRef.current = d;
      d.drive();
    },
    [user, questionnaireCompleted, pathname, orgFeatures, destroyNewDriver, destroySessionDriver],
  );

  runNewPracticeTourRef.current = runNewPracticeTour;

  const runSessionTour = useCallback(
    (isAuto: boolean) => {
      if (!user) return;
      if (isAuto && questionnaireCompleted === false) return;
      const sessionId = parsePracticeSessionId(pathname);
      if (!sessionId) return;
      if (orgFeatures === null) return;

      if (!isAuto) {
        try {
          sessionStorage.removeItem(STORAGE_PENDING_SESSION_MANUAL);
        } catch {
          // ignore
        }
      }

      destroyNewDriver();

      void (async () => {
        try {
          const res = await api.get<{
            session: {
              mode: 'verbal' | 'text';
              visibility?: string;
              status?: string;
              userId?: string;
              inviteEmailsSentAt?: string | null;
              isParticipant?: boolean;
            };
          }>(`/practice/sessions/${sessionId}`);

          const session = res.data.session;
          const canMultiUser = orgFeatures.includes('multi_user');
          const isCreator = Boolean(user.id) && String(session.userId) === String(user.id);
          const canManageAsOrgManager = isOrgManager(user.orgRole);
          const canManageSessionInvites =
            canMultiUser &&
            session.visibility === 'org_open' &&
            session.status === 'active' &&
            (isCreator || canManageAsOrgManager);

          const showInvitePanel = Boolean(canManageSessionInvites);
          const canManageVisibility = canMultiUser && (isCreator || canManageAsOrgManager);
          const showCopyLink =
            canMultiUser && session.visibility === 'org_open' && session.status === 'active';
          const showSessionToolbar =
            Boolean(canManageVisibility && session.status === 'active') || showCopyLink;

          const isOwnSession = Boolean(user.id) && String(session.userId) === String(user.id);
          const isParticipant = Boolean(session.isParticipant);
          const isActive = session.status === 'active';

          const showEndSession = isActive && isOwnSession;

          const showTextComposer =
            session.mode === 'text' && isActive && (isOwnSession || isParticipant);

          const flags: PracticeSessionWalkthroughFlags = {
            mode: session.mode,
            showInvitePanel,
            showSessionToolbar,
            showEndSession,
            showTextComposer,
          };

          const steps = buildPracticeSessionWalkthroughSteps(flags);

          destroySessionDriver();

          const onDestroyed: NonNullable<Config['onDestroyed']> = () => {
            void postPracticeSessionTourComplete();
            sessionDriverRef.current = null;
            setSessionTourVersion(CURRENT_PRACTICE_SESSION_TOUR_VERSION);
          };

          const d = driver({
            showProgress: true,
            smoothScroll: true,
            allowClose: true,
            overlayOpacity: 0.55,
            stagePadding: 8,
            popoverClass: 'akoreps-driver-popover',
            steps,
            onDestroyed,
          });

          sessionDriverRef.current = d;
          window.setTimeout(() => d.drive(), session.mode === 'verbal' ? 500 : 250);
        } catch {
          destroySessionDriver();
        }
      })();
    },
    [user, questionnaireCompleted, pathname, orgFeatures, destroyNewDriver, destroySessionDriver],
  );

  runSessionTourRef.current = runSessionTour;

  useEffect(() => {
    if (pathname !== '/practice/new') {
      autoNewStartedRef.current = false;
      newPracticeScheduleGuardRef.current = false;
      return;
    }
    if (!prefsLoaded || orgFeatures === null) return;

    let manualIntent = false;
    try {
      manualIntent = sessionStorage.getItem(STORAGE_PENDING_NEW_MANUAL) === '1';
    } catch {
      manualIntent = false;
    }

    const wantAuto =
      !manualIntent &&
      questionnaireCompleted === true &&
      newPracticeTourVersion !== null &&
      newPracticeTourVersion < CURRENT_NEW_PRACTICE_TOUR_VERSION;

    if (!manualIntent && !wantAuto) return;
    if (!manualIntent && wantAuto && autoNewStartedRef.current) return;
    if (!manualIntent && wantAuto) autoNewStartedRef.current = true;

    if (newPracticeScheduleGuardRef.current) return;
    newPracticeScheduleGuardRef.current = true;

    const t = window.setTimeout(
      () => runNewPracticeTourRef.current(!manualIntent && wantAuto),
      400,
    );
    return () => {
      window.clearTimeout(t);
      newPracticeScheduleGuardRef.current = false;
    };
  }, [pathname, prefsLoaded, orgFeatures, questionnaireCompleted, newPracticeTourVersion]);

  useEffect(() => {
    const sid = parsePracticeSessionId(pathname);
    if (!sid) {
      autoSessionStartedRef.current = false;
      sessionScheduleGuardRef.current = false;
      return;
    }
    if (!prefsLoaded || orgFeatures === null) return;

    let manualIntent = false;
    try {
      manualIntent = sessionStorage.getItem(STORAGE_PENDING_SESSION_MANUAL) === '1';
    } catch {
      manualIntent = false;
    }

    const wantAuto =
      !manualIntent &&
      questionnaireCompleted === true &&
      sessionTourVersion !== null &&
      sessionTourVersion < CURRENT_PRACTICE_SESSION_TOUR_VERSION;

    if (!manualIntent && !wantAuto) return;
    if (!manualIntent && wantAuto && autoSessionStartedRef.current) return;
    if (!manualIntent && wantAuto) autoSessionStartedRef.current = true;

    if (sessionScheduleGuardRef.current) return;
    sessionScheduleGuardRef.current = true;

    const delay = manualIntent ? 500 : 400;
    const t = window.setTimeout(() => runSessionTourRef.current(!manualIntent && wantAuto), delay);
    return () => {
      window.clearTimeout(t);
      sessionScheduleGuardRef.current = false;
    };
  }, [pathname, prefsLoaded, orgFeatures, questionnaireCompleted, sessionTourVersion]);

  useEffect(() => {
    if (manualNewPracticeRequest < 1) return;
    if (manualNewPracticeRequest === lastManualNewRequestRef.current) return;
    lastManualNewRequestRef.current = manualNewPracticeRequest;
    if (pathname !== '/practice/new') return;
    const t = window.setTimeout(() => runNewPracticeTourRef.current(false), 0);
    return () => window.clearTimeout(t);
  }, [manualNewPracticeRequest, pathname]);

  useEffect(() => {
    if (manualPracticeSessionRequest < 1) return;
    if (manualPracticeSessionRequest === lastManualSessionRequestRef.current) return;
    lastManualSessionRequestRef.current = manualPracticeSessionRequest;
    if (!parsePracticeSessionId(pathname)) return;
    const t = window.setTimeout(() => runSessionTourRef.current(false), 0);
    return () => window.clearTimeout(t);
  }, [manualPracticeSessionRequest, pathname]);

  useEffect(
    () => () => {
      destroyNewDriver();
      destroySessionDriver();
    },
    [destroyNewDriver, destroySessionDriver],
  );

  return null;
}
